import { schema } from 'normalizr';
import { serialize as objectToFormData } from 'object-to-formdata';

import { parseDate, parseDateTime, parseForm, schemaDeserializers } from 'api/deserialize';

import { registerFormErrorAction } from 'store/modules/errors';
import { networkActionTypes } from 'store/utils';

const treatmentSchema = new schema.Entity('treatments');

export const patientSchema = new schema.Entity(
  'patients',
  {
    treatment: treatmentSchema,
  },
  {
    processStrategy: (patient) => ({
      ...patient,
      ...schemaDeserializers({
        birthdate: parseDate,
        enrolledAt: parseDateTime,
        dialysisSince: parseDate,
        updatedAt: parseDateTime,
        createdAt: parseDateTime,
      })(patient),
      activePatientTreatments: patient.activePatientTreatments
        ? patient.activePatientTreatments.map(
            schemaDeserializers({
              updatedAt: parseDateTime,
              createdAt: parseDateTime,
            }),
          )
        : [],
      identifiers: patient.identifiers
        ? patient.identifiers.map(
            schemaDeserializers({
              updatedAt: parseDateTime,
              createdAt: parseDateTime,
            }),
          )
        : [],
      registeredEncounterTypes: patient.registeredEncounterTypes
        ? patient.registeredEncounterTypes.map(
            schemaDeserializers({
              createdAt: parseDateTime,
              updatedAt: parseDateTime,
              deletedAt: parseDateTime,
              encounterFormDefinition: parseForm,
            }),
          )
        : undefined,
    }),
  },
);

export const GET_PATIENT = networkActionTypes('GET_PATIENT');

export const getPatient = (id: string): NetworkAction => ({
  type: 'CALL_API',
  payload: {
    types: GET_PATIENT,
    url: `patients/${id}`,
    method: 'GET',
    normalizeSchema: patientSchema,
  },
});

export const GET_PATIENTS = networkActionTypes('GET_PATIENTS');

export const getPatients = (page: number, sortKeys = 'last_name asc'): NetworkAction => ({
  type: 'CALL_API',
  payload: {
    types: GET_PATIENTS,
    url: 'patients',
    method: 'GET',
    params: { page, pageSize: 30, sortKeys },
    normalizeSchema: { data: [patientSchema] },
  },
});

export const CREATE_PATIENT = networkActionTypes('CREATE_PATIENT');

registerFormErrorAction(CREATE_PATIENT.FAILURE);

export interface CreatePatientArgsT {
  id: string;
  firstName: string;
  lastName: string;
  sex: string;
  birthdate: string;
  caseId: string | null | undefined;
  admissionId: string | null | undefined;
  diagnosis: string;
  renalFailureType: RenalFailureTypeT;
  dialysisInitiated: boolean;
  dialysisSince: Date;
  treatmentId: string;
  treatmentSince: Date;
  careTypes: string[];
  disorder: DisorderT;
  centerIds?: string[];
  residenceType: string;
  referrerType: string | null | undefined;
  careTeamRelations?: { userId: string; relationKind: string }[];
}

export const createPatient = ({
  firstName,
  lastName,
  sex,
  birthdate,
  caseId,
  admissionId,
  diagnosis,
  renalFailureType,
  dialysisInitiated,
  dialysisSince,
  treatmentId,
  treatmentSince,
  careTypes,
  disorder,
  centerIds,
  residenceType,
  referrerType,
  careTeamRelations,
}: CreatePatientArgsT): NetworkAction => ({
  type: 'CALL_API',
  payload: {
    types: CREATE_PATIENT,
    url: 'patients',
    method: 'POST',
    payload: {
      patient: {
        firstName,
        lastName,
        sex,
        birthdate,
        caseId,
        admissionId,
        diagnosisId: diagnosis,
        disorder,
        renalFailureType: renalFailureType || null,
        dialysisInitiated: renalFailureType === 'chronic' ? dialysisInitiated : null,
        dialysisSince: dialysisSince || null,
        treatmentId,
        treatmentSince,
        careTypes,
        centerIds,
        residenceType,
        referrerType: referrerType || null,
        careTeamRelations,
      },
    },
    normalizeSchema: patientSchema,
  },
});

export const UPDATE_PATIENT = networkActionTypes('UPDATE_PATIENT');

registerFormErrorAction(UPDATE_PATIENT.FAILURE);

interface UpdatePatientArgsT {
  id: string;
  firstName: string;
  lastName: string;
  sex: string;
  birthdate: string;
  caseId: string | null | undefined;
  admissionId: string | null | undefined;
  residenceType: string;
  referrerType: string | null | undefined;
}

export const updatePatient = ({
  id,
  firstName,
  lastName,
  sex,
  birthdate,
  caseId,
  admissionId,
  residenceType,
  referrerType,
}: UpdatePatientArgsT): NetworkAction => ({
  type: 'CALL_API',
  payload: {
    types: UPDATE_PATIENT,
    url: `patients/${id}`,
    method: 'PUT',
    payload: {
      patient: {
        firstName,
        lastName,
        sex,
        birthdate,
        caseId,
        admissionId,
        residenceType,
        referrerType,
      },
    },
    normalizeSchema: patientSchema,
  },
});

export const UPDATE_PATIENT_AVATAR = networkActionTypes('UPDATE_PATIENT_AVATAR');

registerFormErrorAction(UPDATE_PATIENT_AVATAR.FAILURE);

interface UpdatePatientAvatarArgsT {
  id: string;
  file: File | null;
}

export const updatePatientAvatar = ({ id, file }: UpdatePatientAvatarArgsT): NetworkAction => ({
  type: 'CALL_API',
  payload: {
    types: UPDATE_PATIENT_AVATAR,
    url: `patients/${id}/avatar`,
    method: file ? 'PUT' : 'DELETE',
    payload: objectToFormData({
      avatar: file,
    }),
    normalizeSchema: patientSchema,
  },
});

export interface UpdateCareTeamArgs {
  patientId: string;
  centerIds: string[];
}

export const UPDATE_CARE_TEAM_SUCCESS = 'UPDATE_CARE_TEAM_SUCCESS';

export const updateCareTeamAction = (payload: UpdateCareTeamArgs) => ({
  type: UPDATE_CARE_TEAM_SUCCESS,
  payload,
});

export const UPDATE_PATIENT_CONTACT_INFO = networkActionTypes('UPDATE_PATIENT_CONTACT_INFO');

registerFormErrorAction(UPDATE_PATIENT_CONTACT_INFO.FAILURE);

interface UpdatePatientContactInfoArgsT {
  id: string;
  contactNotes: string;
}

export const updatePatientContactInfo = ({ id, contactNotes }: UpdatePatientContactInfoArgsT): NetworkAction => ({
  type: 'CALL_API',
  payload: {
    types: UPDATE_PATIENT_CONTACT_INFO,
    url: `patients/${id}/contact_info`,
    method: 'PUT',
    payload: { contactNotes },
    normalizeSchema: patientSchema,
  },
});

export const UPDATE_PATIENT_MEDICAL_INFO = networkActionTypes('UPDATE_PATIENT_MEDICAL_INFO');

registerFormErrorAction(UPDATE_PATIENT_MEDICAL_INFO.FAILURE);

interface UpdatePatientMedicalInfoArgsT {
  id: string;
  bodyHeight: number | null;
  bloodType: string | null;
}

export const updatePatientMedicalInfo = ({
  id,
  bodyHeight,
  bloodType,
}: UpdatePatientMedicalInfoArgsT): NetworkAction => ({
  type: 'CALL_API',
  payload: {
    types: UPDATE_PATIENT_MEDICAL_INFO,
    url: `patients/${id}/medical_info`,
    method: 'PUT',
    payload: {
      patient: {
        bodyHeight,
        bloodType,
      },
    },
    normalizeSchema: patientSchema,
  },
});

export const UPDATE_PATIENT_DISORDER = networkActionTypes('UPDATE_PATIENT_DISORDER');

registerFormErrorAction(UPDATE_PATIENT_DISORDER.FAILURE);

export interface UpdatePatientDisorderArgsT {
  id: string;
  disorder: DisorderT;
  renalFailureType: RenalFailureTypeT | null;
  dialysisInitiated: boolean;
  dialysisSince: Date | null;
}

export const updatePatientDisorder = ({
  id,
  disorder,
  renalFailureType,
  dialysisInitiated,
  dialysisSince,
}: UpdatePatientDisorderArgsT): NetworkAction => ({
  type: 'CALL_API',
  payload: {
    types: UPDATE_PATIENT_DISORDER,
    url: `patients/${id}/disorder`,
    method: 'PUT',
    payload: {
      patientDisorder: {
        disorder,
        renalFailureType: renalFailureType,
        dialysisInitiated: renalFailureType === 'chronic' ? dialysisInitiated : null,
        dialysisSince: dialysisSince,
      },
    },
    actionPayload: { id },
    normalizeSchema: patientSchema,
  },
});

interface UpdatePatientResidenceTypeArgsT {
  id: string;
  residenceType: string;
}

export const UPDATE_PATIENT_RESIDENCE_TYPE = networkActionTypes('UPDATE_PATIENT_RESIDENCE_TYPE');

export const updatePatientResidenceType = ({ id, residenceType }: UpdatePatientResidenceTypeArgsT): NetworkAction => ({
  type: 'CALL_API',
  payload: {
    types: UPDATE_PATIENT_RESIDENCE_TYPE,
    url: `patients/${id}/basic_info`,
    method: 'PUT',
    payload: {
      patient: {
        residenceType,
      },
    },
    normalizeSchema: patientSchema,
  },
});

interface UpdatePatientReferrerTypeArgsT {
  id: string;
  referrerType: string;
}

export const UPDATE_PATIENT_REFERRER_TYPE = networkActionTypes('UPDATE_PATIENT_REFERRER_TYPE');

export const updatePatientReferrerType = ({ id, referrerType }: UpdatePatientReferrerTypeArgsT): NetworkAction => ({
  type: 'CALL_API',
  payload: {
    types: UPDATE_PATIENT_REFERRER_TYPE,
    url: `patients/${id}/basic_info`,
    method: 'PUT',
    payload: {
      patient: {
        referrerType,
      },
    },
    normalizeSchema: patientSchema,
  },
});

interface UpdatePatientPhoneArgsT {
  id: string;
  phone: string;
}

export const UPDATE_PATIENT_PHONE = networkActionTypes('UPDATE_PATIENT_PHONE');

export const updatePatientPhone = ({ id, phone }: UpdatePatientPhoneArgsT): NetworkAction => ({
  type: 'CALL_API',
  payload: {
    types: UPDATE_PATIENT_PHONE,
    url: `patients/${id}`,
    method: 'PUT',
    payload: {
      patient: {
        phone,
      },
    },
    normalizeSchema: patientSchema,
  },
});

export const DEACTIVATE_PATIENT = networkActionTypes('DEACTIVATE_PATIENT');

registerFormErrorAction(DEACTIVATE_PATIENT.FAILURE);

interface DeactivatePatientArgsT {
  id: string;
  reason: string;
  note: string;
}

export const deactivatePatient = ({ id, reason, note }: DeactivatePatientArgsT): NetworkAction => ({
  type: 'CALL_API',
  payload: {
    types: DEACTIVATE_PATIENT,
    url: 'unenrollments',
    method: 'POST',
    payload: {
      unenrollment: {
        patientId: id,
        reason,
        note,
      },
    },
  },
});

export const ENROLL_EMERGENCY_PATIENT = networkActionTypes('ENROLL_EMERGENCY_PATIENT');

registerFormErrorAction(ENROLL_EMERGENCY_PATIENT.FAILURE);

interface EnrollEmergencyPatientArgsT {
  firstName: string;
  lastName: string;
  sex: string;
  birthdate: Date;
  diagnosis: string;
  renalFailureType: RenalFailureTypeT;
  dialysisInitiated: boolean;
  dialysisSince: Date;
  treatmentId: string;
  modality: string | undefined;
  careType: string;
  disorder: DisorderT;
  centerIds?: string[];
  residenceType: string;
  referrerType: string;
  careTeamRelations?: { userId: string; relationKind: string }[];
}

export const enrollEmergencyPatient = ({
  firstName,
  lastName,
  sex,
  birthdate,
  diagnosis,
  renalFailureType,
  dialysisInitiated,
  dialysisSince,
  disorder,
  centerIds,
  residenceType,
  referrerType,
  careTeamRelations,
}: EnrollEmergencyPatientArgsT): NetworkAction => ({
  type: 'CALL_API',
  payload: {
    types: ENROLL_EMERGENCY_PATIENT,
    url: 'emergency_enrollments',
    method: 'POST',
    payload: {
      emergencyPatient: {
        firstName,
        lastName,
        sex,
        birthdate,
        residenceType,
        referrerType,
      },
      enrollment: {
        diagnosisId: diagnosis,
        disorder,
        renalFailureType: renalFailureType || null,
        dialysisInitiated: renalFailureType === 'chronic' ? dialysisInitiated : null,
        dialysisSince: dialysisSince || null,
        centerIds,
        careTeamRelations,
      },
    },
    normalizeSchema: { patient: patientSchema },
  },
});

export const ENROLL_PATIENT = networkActionTypes('ENROLL_PATIENT');

registerFormErrorAction(ENROLL_PATIENT.FAILURE);

export interface EnrollPatientArgsT {
  patientId: string;
  diagnosis: string;
  renalFailureType: RenalFailureTypeT;
  dialysisInitiated: boolean;
  dialysisSince: Date;
  disorder: DisorderT;
  centerIds?: string[];
  residenceType: string;
  referrerType?: string;
  careTeamRelations?: { userId: string; relationKind: string }[];
}

export const enrollPatient = ({
  patientId,
  diagnosis,
  renalFailureType,
  dialysisInitiated,
  dialysisSince,
  disorder,
  centerIds,
  residenceType,
  referrerType,
  careTeamRelations,
}: EnrollPatientArgsT): NetworkAction => ({
  type: 'CALL_API',
  payload: {
    types: ENROLL_PATIENT,
    url: 'enrollments',
    method: 'POST',
    payload: {
      enrollment: {
        patientId,
        diagnosisId: diagnosis,
        disorder,
        renalFailureType: renalFailureType || null,
        dialysisInitiated: renalFailureType === 'chronic' ? dialysisInitiated : false,
        dialysisSince: dialysisSince || null,
        centerIds,
        careTeamRelations,
      },
      patient: {
        residenceType,
        referrerType,
      },
    },
  },
});

export const MERGE_PATIENTS = networkActionTypes('MERGE_PATIENTS');

registerFormErrorAction(MERGE_PATIENTS.FAILURE);

export interface MergePatientsArgsT {
  emergencyPatientId: string;
  ehrPatientId: string;
}

export const mergePatients = ({ emergencyPatientId, ehrPatientId }: MergePatientsArgsT): NetworkAction => ({
  type: 'CALL_API',
  payload: {
    types: MERGE_PATIENTS,
    url: 'merge_emergency_patients',
    method: 'POST',
    payload: {
      patients: {
        emergencyPatientId,
        ehrPatientId,
      },
    },
    normalizeSchema: patientSchema,
  },
});

export const UPDATE_PATIENT_ALLERGIES_KNOWN = networkActionTypes('UPDATE_PATIENT_ALLERGIES_KNOWN');

export interface updatePatientAllergiesKnownArgsT {
  id: string;
  allergiesKnown: 'true' | 'false' | null;
}

export const updatePatientAllergiesKnown = ({
  id,
  allergiesKnown,
}: updatePatientAllergiesKnownArgsT): NetworkAction => ({
  type: 'CALL_API',
  payload: {
    types: UPDATE_PATIENT_ALLERGIES_KNOWN,
    url: `patients/${id}/allergies_known`,
    method: 'PUT',
    payload: {
      patient: {
        allergiesKnown,
      },
    },
    actionPayload: { id },
    normalizeSchema: patientSchema,
  },
});

export const UPDATE_PATIENT_HOME_NURSING = networkActionTypes('UPDATE_PATIENT_HOME_NURSING');

export interface updatePatientHomeNursingArgsT {
  id: string;
  receivesHomeNursing: boolean;
}

export const updatePatientHomeNursing = ({
  id,
  receivesHomeNursing,
}: updatePatientHomeNursingArgsT): NetworkAction => ({
  type: 'CALL_API',
  payload: {
    types: UPDATE_PATIENT_HOME_NURSING,
    url: `patients/${id}/home_nursing`,
    method: 'PUT',
    payload: {
      patient: {
        receivesHomeNursing,
      },
    },
    actionPayload: { id },
    normalizeSchema: patientSchema,
  },
});

export const REPLACE_ACTIVE_PATIENT_TREATMENTS = 'REPLACE_ACTIVE_PATIENT_TREATMENTS';

export const replaceActivePatientTreatments = (patientId: string, activePatientTreatments: Record<string, any>[]) => ({
  type: REPLACE_ACTIVE_PATIENT_TREATMENTS,
  payload: {
    patientId,
    activePatientTreatments,
  },
});

export const GET_PATIENT_ALLERGY_INFO = networkActionTypes('GET_PATIENT_ALLERGY_INFO');

export const getPatientAllergyInfo = (patientId: string): NetworkAction => ({
  type: 'CALL_API',
  payload: {
    types: GET_PATIENT_ALLERGY_INFO,
    url: `patients/${patientId}/allergies_info`,
    method: 'GET',
    actionPayload: { patientId },
  },
});
